import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { getErrorMessage, getFieldErrors } from '@/lib/errors';
import { serversApi } from '@/lib/servers-api';
import { cn } from '@/lib/utils';
import type { Server, ServerGroup, SshCredential } from '@/types/api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Null creates a new server; a server edits it. */
  server: Server | null;
  credentials: SshCredential[];
  groups: ServerGroup[];
  /**
   * Called with the saved record. Saving now also tests the connection, and a server that
   * saved but could not be reached is still a failure the customer has to be told about —
   * the dialog closing on its own would read as success.
   */
  onSaved?: ((server: Server) => void) | undefined;
}

type SecretMode = 'password' | 'private_key' | 'saved';

const EMPTY = {
  ssh_host: '',
  ssh_port: '22',
  ssh_user: 'root',
  secret: '',
  passphrase: '',
  sudo_password: '',
  credential_id: '',
  name: '',
  group_id: '',
  tags: '',
  notes: '',
};

/**
 * Add / edit a server.
 *
 * The whole dialog is three questions: where is it, who do I log in as, and what is
 * the password. Everything else — name, group, tags, notes — is optional and folded
 * away, because it is not what anyone is thinking about when they add their first
 * machine. The credential is created behind the scenes; the customer never has to know
 * it is a separate record.
 *
 * ## Root is detected, not chosen
 *
 * The account can be root, or any account that can use sudo. Nobody is asked which: the
 * connection check finds out, and the server's row says what it found. The one thing that
 * cannot be detected is a sudo password for a key-based login, so that is the one extra,
 * optional field — write-only, like every other secret here.
 */
export function ServerFormDialog({
  open,
  onOpenChange,
  server,
  credentials,
  groups,
  onSaved,
}: Props) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [mode, setMode] = useState<SecretMode>('password');
  const [showOptional, setShowOptional] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Re-seed on open so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;

    setFieldErrors({});
    setShowOptional(false);
    setMode(server?.credential_id ? 'saved' : 'password');
    setForm(
      server
        ? {
            ...EMPTY,
            ssh_host: server.ssh_host,
            ssh_port: String(server.ssh_port),
            ssh_user: server.ssh_user,
            credential_id: server.credential_id ?? '',
            name: server.name,
            group_id: server.group_id ?? '',
            tags: server.tags.join(', '),
            notes: server.notes,
          }
        : EMPTY,
    );
  }, [open, server]);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        ssh_host: form.ssh_host.trim(),
        ssh_port: Number(form.ssh_port) || 22,
        ssh_user: form.ssh_user.trim() || 'root',
        name: form.name.trim() || undefined,
        group_id: form.group_id || null,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        notes: form.notes,
        ...(mode === 'saved'
          ? { credential_id: form.credential_id || '' }
          : form.secret.trim()
            ? {
                credential_secret: form.secret,
                credential_auth_type: mode,
                ...(form.passphrase ? { credential_passphrase: form.passphrase } : {}),
                ...(mode === 'private_key' && form.sudo_password
                  ? { credential_sudo_password: form.sudo_password }
                  : {}),
              }
            : {}),
      };

      return server ? serversApi.update(server.id, payload) : serversApi.create(payload);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['servers'] });
      void queryClient.invalidateQueries({ queryKey: ['server-inventory'] });
      onOpenChange(false);
      onSaved?.(saved);
    },
    onError: (error) => setFieldErrors(getFieldErrors(error)),
  });

  const endpointChanged =
    server !== null &&
    (server.ssh_host !== form.ssh_host.trim() ||
      String(server.ssh_port) !== form.ssh_port ||
      server.ssh_user !== form.ssh_user.trim());

  const set = (key: keyof typeof EMPTY) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{server ? 'Edit server' : 'Add a server'}</DialogTitle>
          <DialogDescription>
            The platform connects, detects the operating system, and checks how the account can act as
            root. Nothing on the server is changed.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setFieldErrors({});
            save.mutate();
          }}
        >
          {save.error && Object.keys(fieldErrors).length === 0 && (
            <Alert variant="danger">{getErrorMessage(save.error)}</Alert>
          )}

          {/* ── Where is it ── */}
          <div className="grid gap-4 sm:grid-cols-[1fr_7rem]">
            <div className="space-y-2">
              <Label htmlFor="sf-host">Hostname or IP address</Label>
              <Input
                id="sf-host"
                value={form.ssh_host}
                onChange={(e) => set('ssh_host')(e.target.value)}
                required
                autoFocus
                autoComplete="off"
              />
              {fieldErrors.ssh_host && <p className="text-xs text-danger">{fieldErrors.ssh_host}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="sf-port">SSH port</Label>
              <Input
                id="sf-port"
                type="number"
                min={1}
                max={65535}
                value={form.ssh_port}
                onChange={(e) => set('ssh_port')(e.target.value)}
              />
            </div>
          </div>

          {/* ── Who do I log in as ── */}
          <div className="space-y-2">
            <Label htmlFor="sf-user">Username</Label>
            <Input
              id="sf-user"
              value={form.ssh_user}
              onChange={(e) => set('ssh_user')(e.target.value)}
              autoComplete="off"
              required
            />
            {fieldErrors.ssh_user ? (
              <p className="text-xs text-danger">{fieldErrors.ssh_user}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                root, or an account that can use sudo. The platform detects which when it connects.
              </p>
            )}
          </div>

          {/* ── What is the secret ── */}
          {mode === 'saved' ? (
            <div className="space-y-2">
              <Label htmlFor="sf-cred">Saved credential</Label>
              <Select
                id="sf-cred"
                value={form.credential_id}
                onChange={(e) => set('credential_id')(e.target.value)}
              >
                <option value="">No credential</option>
                {credentials.map((credential) => (
                  <option key={credential.id} value={credential.id}>
                    {credential.name} ({credential.auth_type_label} · {credential.username})
                  </option>
                ))}
              </Select>
              <ModeSwitch
                onSelect={setMode}
                options={[
                  ['password', 'Type a password instead'],
                  ['private_key', 'Paste an SSH key instead'],
                ]}
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="sf-secret">{mode === 'password' ? 'Password' : 'Private key'}</Label>
              {mode === 'password' ? (
                <Input
                  id="sf-secret"
                  type="password"
                  value={form.secret}
                  onChange={(e) => set('secret')(e.target.value)}
                  autoComplete="new-password"
                />
              ) : (
                <Textarea
                  id="sf-secret"
                  className="min-h-36 font-mono text-xs"
                  value={form.secret}
                  onChange={(e) => set('secret')(e.target.value)}
                  spellCheck={false}
                  autoComplete="off"
                />
              )}
              {fieldErrors.credential_secret ? (
                <p className="text-xs text-danger">{fieldErrors.credential_secret}</p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  {mode === 'private_key' ? 'Paste the whole private key, including its BEGIN and END lines. ' : ''}
                  {server ? 'Leave empty to keep the current one. ' : ''}
                  Encrypted before it is stored, and never shown again.
                </p>
              )}

              {mode === 'private_key' && (
                <>
                  <div className="space-y-2 pt-2">
                    <Label htmlFor="sf-passphrase">
                      Key passphrase{' '}
                      <span className="font-normal text-muted-foreground">(only if the key is encrypted)</span>
                    </Label>
                    <Input
                      id="sf-passphrase"
                      type="password"
                      value={form.passphrase}
                      onChange={(e) => set('passphrase')(e.target.value)}
                      autoComplete="new-password"
                    />
                  </div>
                  {form.ssh_user.trim() !== 'root' && (
                    <div className="space-y-2 pt-2">
                      <Label htmlFor="sf-sudo">
                        Sudo password{' '}
                        <span className="font-normal text-muted-foreground">(only if sudo asks this account for one)</span>
                      </Label>
                      <Input
                        id="sf-sudo"
                        type="password"
                        value={form.sudo_password}
                        onChange={(e) => set('sudo_password')(e.target.value)}
                        autoComplete="new-password"
                      />
                    </div>
                  )}
                  {fieldErrors.credential_sudo_password && (
                    <p className="text-xs text-danger">{fieldErrors.credential_sudo_password}</p>
                  )}
                </>
              )}

              <ModeSwitch
                onSelect={setMode}
                options={[
                  mode === 'password'
                    ? (['private_key', 'Paste an SSH key instead'] as const)
                    : (['password', 'Type a password instead'] as const),
                  ...(credentials.length > 0
                    ? [['saved', `Use a saved credential (${credentials.length})`] as const]
                    : []),
                ]}
              />
            </div>
          )}

          {endpointChanged && (
            <Alert variant="warning">
              You have changed the address, port, or username. Because that may point at a
              different machine, the detected operating system and status will be cleared and the
              server will need testing again.
            </Alert>
          )}

          {/* ── Everything else, folded away ── */}
          <div className="border-t border-border pt-3">
            <button
              type="button"
              onClick={() => setShowOptional((v) => !v)}
              className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {showOptional ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
              Name, group, tags, and notes
            </button>

            {showOptional && (
              <div className="mt-4 flex flex-col gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sf-name">Display name</Label>
                  <Input
                    id="sf-name"
                    value={form.name}
                    onChange={(e) => set('name')(e.target.value)}
                  />
                  {fieldErrors.name ? (
                    <p className="text-xs text-danger">{fieldErrors.name}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Leave empty to use the address.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sf-group">Group</Label>
                  <Select
                    id="sf-group"
                    value={form.group_id}
                    onChange={(e) => set('group_id')(e.target.value)}
                  >
                    <option value="">Ungrouped</option>
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sf-tags">Tags</Label>
                  <Input
                    id="sf-tags"
                    value={form.tags}
                    onChange={(e) => set('tags')(e.target.value)}
                  />
                  {fieldErrors.tags ? (
                    <p className="text-xs text-danger">{fieldErrors.tags}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Separate tags with commas.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sf-notes">Notes</Label>
                  <Textarea
                    id="sf-notes"
                    value={form.notes}
                    onChange={(e) => set('notes')(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {server ? 'Save changes' : 'Add server'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Small text links for swapping how the secret is supplied. */
function ModeSwitch({
  onSelect,
  options,
}: {
  onSelect: (mode: SecretMode) => void;
  options: readonly (readonly [SecretMode, string])[];
}) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
      {options.map(([value, label]) => (
        <button
          key={value}
          type="button"
          onClick={() => onSelect(value)}
          className={cn(
            'text-xs text-primary transition-opacity hover:underline',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
